import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterSelectedValue } from '../../../hooks/filters/interfaces/filter-selected-value.interface';
import { Filter } from '../../../hooks/filters/interfaces/filter.interface';
import { useAppUpdateAllFilterSelectedValue } from '../../../hooks/filters/use-app-filter-selected-value.hook';
import { useAppFilters } from '../../../hooks/filters/use-app-filters.hook';

import { MODULE_ID } from '../../reports/constants/module-id.constants';

export interface FilterContextData {
  filters: Filter[];
  moduleId: string;
  ready: boolean;
  initialFilterSelection: FilterSelectedValue[];
}
export const AppFilterContext = createContext<FilterContextData>({
  filters: [],
  moduleId: MODULE_ID.DEFAULT,
  ready: false,
  initialFilterSelection: [],
});
export interface AppFilterProviderProps extends PropsWithChildren {
  filterSelection: FilterSelectedValue[];
  moduleId: string;
}
/**
 * Loads filters only once. It uses loaded filters to get metadata and default filter values.
 *
 */
export const AppFilterProvider = ({
  children,
  filterSelection,
  moduleId,
}: AppFilterProviderProps) => {
  const [ready, setReady] = useState(false);
  const { filters } = useAppFilters(filterSelection);
  const updateFilters = useAppUpdateAllFilterSelectedValue();

  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    if (!filters || filters.length === 0) return;
    seeded.current = true;
    updateFilters(filters);
    setReady(true);
  }, [filters, updateFilters]);

  const filterContext = useMemo((): FilterContextData => {
    if (filters && filters.length > 0 && ready)
      return {
        filters,
        moduleId,
        ready: ready,
        initialFilterSelection: filterSelection,
      };
    else
      return {
        filters: [],
        moduleId,
        ready: false,
        initialFilterSelection: [],
      };
  }, [filters, moduleId, ready, filterSelection]);
  return (
    <AppFilterContext.Provider value={filterContext}>
      {children}
    </AppFilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = useContext(AppFilterContext);
  // Only take shared filters and the module specific ones.
  const filters = useMemo(() => {
    // Any filter that is not module specific.
    const filters = context.filters.filter(
      (i) =>
        !i.tags?.find((j) => j.match(/#(.*)#/g) !== null) ||
        // Or, those belongs to the current module.
        i.tags?.find((j) => j === `#${context.moduleId}#`),
    );

    return filters;
  }, [context]);
  return filters;
};

export const useFilterReadyContext = () => {
  const context = useContext(AppFilterContext);
  return context.ready;
};

export const useFilterInitialFilterSelectionContext = () => {
  const context = useContext(AppFilterContext);
  return context.initialFilterSelection;
};
